import { FC, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import classes from './sanctionSequenceReset.module.scss'

interface IProps {
    value?: number | null;
    canEdit: boolean;
    onUpdate: (value: number) => Promise<void>;
    onSuccess: (value: number) => void;
}

const normalizeValue = (value?: number | null): number => (
    typeof value === 'number' && Number.isFinite(value) ? value : 0
)

const validateValue = (value: string): string => {
    if(value.trim() === ''){
        return 'Укажите значение счётчика'
    }

    const parsedValue = Number(value)
    if(!Number.isFinite(parsedValue) || !Number.isInteger(parsedValue)){
        return 'Введите целое число'
    }

    if(parsedValue < 0){
        return 'Значение не может быть отрицательным'
    }

    return ''
}

export const SanctionSequenceResetAction: FC<IProps> = ({value, canEdit, onUpdate, onSuccess}) => {
    const [isEditOpen, setIsEditOpen] = useState(false)
    const [inputValue, setInputValue] = useState(() => String(normalizeValue(value)))
    const [inputError, setInputError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [isForbidden, setIsForbidden] = useState(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    useEffect(() => {
        setIsForbidden(false)
    }, [canEdit])

    useEffect(() => {
        if(isEditOpen){
            setInputValue(String(normalizeValue(value)))
            setInputError('')
        }
    }, [isEditOpen, value])

    const save = async () => {
        const error = validateValue(inputValue)
        if(error){
            setInputError(error)
            return
        }

        const nextValue = Number(inputValue)
        try{
            setIsSaving(true)
            await onUpdate(nextValue)
            onSuccess(nextValue)
            setIsEditOpen(false)
            setGlobalMessage({type: 'ok', message: 'Счётчик нарушений обновлён'})
        }
        catch(error){
            if(error instanceof AuthError){
                if(error.status === 401){
                    setIsAuth(false)
                }
                if(error.status === 403){
                    setIsForbidden(true)
                }
                setGlobalMessage({type: 'error', message: error.message})
            }
            else if(error instanceof MyError){
                setGlobalMessage({type: 'error', message: error.message})
            }
            else{
                setGlobalMessage({type: 'error', message: 'Не удалось обновить счётчик нарушений'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    if(!canEdit || isForbidden){
        return null
    }

    return <>
        <MyButton onClick={() => setIsEditOpen(true)}>Редактировать</MyButton>
        <Modal open={isEditOpen} setOpen={setIsEditOpen}>
            <section className={classes.modalContent} role="dialog" aria-modal="true" aria-label="Редактировать счётчик нарушений">
                <h3 className={classes.modalTitle}>Редактировать счётчик нарушений</h3>
                <MyInput
                    title="Количество нарушений"
                    type="number"
                    min={0}
                    step={1}
                    value={inputValue}
                    setValue={setInputValue}
                    error={inputError}
                    setError={setInputError}
                    isLoading={isSaving}
                />
                <section className={classes.modalActions}>
                    <MyButtonSecondary onClick={() => setIsEditOpen(false)} disabled={isSaving}>Отмена</MyButtonSecondary>
                    <MyButton onClick={save} isLoading={isSaving}>Сохранить</MyButton>
                </section>
            </section>
        </Modal>
    </>
}
